import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Dev-Password': 'secure2025dev'
        }
      });

      if (response.status === 401 || response.status === 403) {
        setError('Admin access required');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-red-400 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Site Analytics</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Back to Home
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-6">
            <div className="text-blue-200 text-sm mb-2">Total Visits</div>
            <div className="text-white text-4xl font-bold">{analytics.total_visits}</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-6">
            <div className="text-green-200 text-sm mb-2">Unique Visitors</div>
            <div className="text-white text-4xl font-bold">{analytics.unique_visitors}</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg p-6">
            <div className="text-purple-200 text-sm mb-2">Last 30 Days</div>
            <div className="text-white text-4xl font-bold">{analytics.last_30_days.visits}</div>
            <div className="text-purple-200 text-xs mt-1">{analytics.last_30_days.unique_visitors} unique</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-lg p-6">
            <div className="text-orange-200 text-sm mb-2">Last 24 Hours</div>
            <div className="text-white text-4xl font-bold">{analytics.last_24_hours.visits}</div>
            <div className="text-orange-200 text-xs mt-1">{analytics.last_24_hours.unique_visitors} unique</div>
          </div>
        </div>

        {/* Time Period Stats */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Breakdown by Time Period</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-gray-400 text-sm mb-1">Last 7 Days</div>
              <div className="text-white text-2xl font-bold">{analytics.last_7_days.visits} visits</div>
              <div className="text-gray-400 text-sm">{analytics.last_7_days.unique_visitors} unique visitors</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm mb-1">Last 30 Days</div>
              <div className="text-white text-2xl font-bold">{analytics.last_30_days.visits} visits</div>
              <div className="text-gray-400 text-sm">{analytics.last_30_days.unique_visitors} unique visitors</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm mb-1">All Time</div>
              <div className="text-white text-2xl font-bold">{analytics.total_visits} visits</div>
              <div className="text-gray-400 text-sm">{analytics.unique_visitors} unique visitors</div>
            </div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Top Pages</h2>
          {analytics.top_pages.length === 0 ? (
            <p className="text-gray-400">No page data yet</p>
          ) : (
            <div className="space-y-3">
              {analytics.top_pages.map((page, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div className="text-white font-mono">{page.path || '/'}</div>
                  <div className="text-gray-400">{page.visits} visits</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Visits */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Recent Visits (Last 50)</h2>
          {analytics.recent_visits.length === 0 ? (
            <p className="text-gray-400">No visits yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Page</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Timestamp</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recent_visits.map((visit) => (
                    <tr key={visit.id} className="border-b border-gray-700 hover:bg-gray-750">
                      <td className="py-3 px-4 text-white font-mono text-sm">{visit.path}</td>
                      <td className="py-3 px-4 text-gray-300 text-sm">
                        {new Date(visit.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm">{visit.ip_address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
